import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { catchError, Observable, of } from 'rxjs';
import { ManualApprovalService } from '../services/manual-approval.service';
import { TicketData } from '../../../shared/models/ticket.model';

/**
 * Detail-screen resolver (AB#8967). Runs BEFORE the component activates so a
 * hard refresh or a direct/bookmarked deep-link — where the manual-approval
 * backlog signal is empty because it is only ever populated by a live
 * SignalR event or the list screen's poller — still has ticket data by the
 * time the screen reads it. `ManualApprovalService.getTicketData` re-hydrates
 * the backlog from the held-queue endpoint when the id isn't already
 * cached, then resolves either way.
 *
 * Resolves to `undefined` for a genuinely unknown/expired id or a failed
 * fetch — it never throws and blocks navigation. The detail screen's guard
 * effect (AB#8972) reacts to that undefined value and redirects with a
 * notice instead of rendering a blank, still-actionable ticket.
 */
export const ManualApprovalDetailResolver: ResolveFn<
    TicketData | undefined
> = (route: ActivatedRouteSnapshot): Observable<TicketData | undefined> => {
    const manualApprovalService = inject(ManualApprovalService);
    const suggestionId = Number(route.paramMap.get('id'));

    manualApprovalService.loadSelectableData();

    return manualApprovalService.getTicketData(suggestionId).pipe(
        catchError((error) => {
            console.error('Error resolving manual-approval ticket:', error);
            return of(undefined);
        }),
    );
};
